import { URL_WITH_PROTOCOL_REGEX } from '@constants/regex';
import { URL_REMOVAL_FLAG } from '@conversed/rest/src/contracts/llm-usages/check-results';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TDraftState } from '../conversation-review-modal.type';

export type TCorrectionErrors = {
  answer: boolean;
  url: boolean;
};

const EMPTY_CORRECTION_ERRORS: TCorrectionErrors = {
  answer: false,
  url: false,
};

export const useReviewValidation = () => {
  const { t } = useTranslation();

  // Field-level validation flags for the correction section (NEEDS_IMPROVEMENT).
  // Each flag is set when the specific field fails validation and cleared when
  // the user edits that field, giving precise per-field feedback.
  const [correctionErrors, setCorrectionErrors] = useState<TCorrectionErrors>(
    EMPTY_CORRECTION_ERRORS,
  );

  const clearCorrectionErrors = useCallback(() => {
    setCorrectionErrors(EMPTY_CORRECTION_ERRORS);
  }, []);

  const clearAnswerError = useCallback(() => {
    setCorrectionErrors((prev) => ({ ...prev, answer: false }));
  }, []);

  const clearUrlError = useCallback(() => {
    setCorrectionErrors((prev) => ({ ...prev, url: false }));
  }, []);

  // Validation that runs when the user attempts to save a NEEDS_IMPROVEMENT review.
  // Throws Errors with translated messages that the outer save flow catches + surfaces
  // via toastErrorTranslated.
  const validateNeedsImprovement = useCallback(
    (draft: TDraftState) => {
      // 1. Correction answer is required
      if (!draft.correctedAnswer?.trim()) {
        setCorrectionErrors((prev) => ({ ...prev, answer: true }));
        throw new Error(
          t('train-ai.fact-checking.error.corrected-answer-required'),
        );
      }
      // 2. If a URL is provided, it must be a valid URL (skip the URL_REMOVAL_FLAG sentinel)
      const trimmedURL = draft.correctedURL?.trim();
      if (
        trimmedURL &&
        trimmedURL !== URL_REMOVAL_FLAG &&
        !URL_WITH_PROTOCOL_REGEX.test(trimmedURL)
      ) {
        setCorrectionErrors((prev) => ({ ...prev, url: true }));
        throw new Error(
          t('train-ai.fact-checking.error.corrected-url-invalid'),
        );
      }
      // 3. If the user enabled "report issue", they must pick at least one issue type.
      // Scroll to the section so the missing-selection is visible to the reviewer.
      // Explicitly scroll the modal's card-scroll container - using Element.scrollIntoView
      // would walk up the DOM and also scroll any other scrollable ancestor (the modal
      // panel itself becomes scrollable when content exceeds its max-height), leaving an
      // empty gap at the bottom of the panel after the scroll.
      if (draft.reportIssue && !draft.reportIssueParams.categories.length) {
        const reportIssueSection = document.getElementById(
          'conversation-review-report-issue',
        );
        const scrollContainer = reportIssueSection?.closest(
          '.conversation-review-modal__card-scroll',
        );
        if (reportIssueSection && scrollContainer instanceof HTMLElement) {
          const targetRect = reportIssueSection.getBoundingClientRect();
          const containerRect = scrollContainer.getBoundingClientRect();
          scrollContainer.scrollTo({
            top:
              scrollContainer.scrollTop + (targetRect.top - containerRect.top),
            behavior: 'smooth',
          });
        }
        throw new Error(
          t('train-ai.fact-checking.report-issue.category.required'),
        );
      }
    },
    [t],
  );

  return {
    correctionErrors,
    setCorrectionErrors,
    clearCorrectionErrors,
    clearAnswerError,
    clearUrlError,
    validateNeedsImprovement,
  };
};
